import { type FC, useContext } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Stack, styled, type SxProps, type Theme } from "@mui/system";
import { LinearProgress, useTheme } from "@mui/material";

import Card from "@/components/card";
import { BodyBook, Header1, SmallRegular } from "@/components/typography";
import { InProgressBatchesActionMenu } from "@/views/production/orders/in-production-orders-card/in-progress-batches-action-menu";
import { getJobContentsString } from "@/views/production/orders/utils/utils";
import { type ActiveBatch } from "@/api/selection-api";

import { OrdersContext } from "../orders.context";

const StyledCard = styled(Card)`
	&&& {
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		flex-shrink: 0;
		padding: 0;
		background-color: ${({ theme }) => theme.palette.background.default};
		max-height: 88px;
	}
`;

const StyledHeader1 = styled(Header1)`
	text-overflow: ellipsis;
	overflow: hidden;
	white-space: nowrap;
`;

const GreySmallRegular = styled(SmallRegular)`
	color: ${(props) => props.theme.palette.text.secondary};
`;

interface ColumnProps {
	readonly messageId: string;
	readonly value: string;
	readonly sx?: SxProps<Theme>;
}

const Column: FC<ColumnProps> = ({ messageId, value, sx }) => {
	return (
		<Stack direction="column" gap={2} sx={sx}>
			<GreySmallRegular>
				<FormattedMessage id={messageId} defaultMessage="{messageId}" values={{ messageId }} />
			</GreySmallRegular>
			<BodyBook>{value}</BodyBook>
		</Stack>
	);
};

export const getStatusBarColor = (orderStatus: string): "grey" | "green" => {
	return orderStatus === "Paused" || orderStatus === "Pausing" || orderStatus === "Waiting to trigger"
		? "grey"
		: "green";
};

interface InProductionOrderProps {
	readonly selectedBatch: ActiveBatch;
}

export const InProductionOrder: FC<InProductionOrderProps> = ({ selectedBatch }) => {
	const intl = useIntl();
	const theme = useTheme();
	const { cartonCountPerJob, labelCountPerJob, cartonDimension } = selectedBatch;
	const hideContentColumn = cartonCountPerJob === undefined && labelCountPerJob === undefined;

	const { scanToTriggerConfigured } = useContext(OrdersContext);

	return (
		<StyledCard>
			<Stack sx={{ m: 1 }} justifyContent="space-between" direction="row">
				<Stack sx={{ width: "30%" }}>
					<StyledHeader1>{selectedBatch.alias}</StyledHeader1>
				</Stack>
				{scanToTriggerConfigured && selectedBatch.licensePlateNumber ? (
					<Column messageId="Trigger Value" value={selectedBatch.licensePlateNumber} />
				) : null}
				{!hideContentColumn && (
					<Column
						messageId="Contents"
						value={getJobContentsString({ cartonCountPerJob, labelCountPerJob, cartonDimension, intl })}
					/>
				)}
				<Column messageId="Quantity" value={`${selectedBatch.completedCount}/${selectedBatch.quantity}`} />
				{selectedBatch.packStation ? <Column messageId="PS" value={selectedBatch.packStation} /> : null}
				<Stack gap={2} direction="row">
					<Column
						messageId="Status"
						value={intl.formatMessage({ id: selectedBatch.status, defaultMessage: "Unknown status" })}
						sx={{ minWidth: 150, alignItems: "flex-end" }}
					/>
					<Stack alignItems="center" justifyContent="center">
						<InProgressBatchesActionMenu batchId={selectedBatch.batchId} alias={selectedBatch.alias} />
					</Stack>
				</Stack>
			</Stack>
			<LinearProgress
				variant="determinate"
				value={(selectedBatch.completedCount / selectedBatch.quantity) * 100}
				color="success"
				sx={{ backgroundColor: theme.palette.background.default, height: 6, borderRadius: "0px 0px 5px 5px" }}
			/>
		</StyledCard>
	);
};
